import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightLeftIcon, CheckIcon } from 'lucide-react';
interface MoveFieldMenuProps {
  fieldLabel: string;
  currentSectionTitle: string;
  targets: {
    id: string;
    title: string;
  }[];
  onMove: (sectionId: string) => void;
}

/**
 * Compact "move this field to another section" menu.
 * Uses fixed positioning so it escapes the scrollable field list.
 */
export function MoveFieldMenu({
  fieldLabel,
  currentSectionTitle,
  targets,
  onMove
}: MoveFieldMenuProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScroll = () => setOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);
  const toggle = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const width = 224;
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      setCoords({
        top: rect.bottom + 6,
        left
      });
    }
    setOpen(o => !o);
  };
  return <>
      <button ref={buttonRef} type="button" onClick={toggle} aria-haspopup="menu" aria-expanded={open} aria-label={`Move ${fieldLabel} to another section`} className={`flex h-6 shrink-0 items-center gap-1 rounded-md border px-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors ${open ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`} data-id="element-59" data-mp="38deijg9hagmd">
        <ArrowRightLeftIcon className="h-3 w-3" data-id="element-60" data-mp="zud7v1rm3exx" />
        Move
      </button>

      {open && <>
          <div className="fixed inset-0 z-40" aria-hidden="true" onClick={() => setOpen(false)} data-id="element-61" data-mp="v3eoftbbu0qg" />
          <div role="menu" aria-label={`Move ${fieldLabel}`} style={{
        top: coords.top,
        left: coords.left,
        width: 224
      }} className="fixed z-50 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl" data-id="element-62" data-mp="q3scdn78iqj8">
            <p className="border-b border-slate-100 px-3 py-2" data-id="element-63" data-mp="c40tb81v2jyd">
              <span className="block truncate text-xs font-semibold text-slate-800" data-id="element-64" data-mp="75phwsvx41e1">
                {fieldLabel}
              </span>
              <span className="block truncate text-[11px] text-slate-400" data-id="element-65" data-mp="26j39ug8xmrp5">
                Currently in {currentSectionTitle}
              </span>
            </p>
            <ul className="max-h-56 overflow-y-auto py-1" data-id="element-66" data-mp="2939givoxkw9e">
              {targets.map(t => <li key={t.id} data-id="element-67" data-mp="2e2clyh8tp4hw">
                  <button type="button" role="menuitem" onClick={() => {
              onMove(t.id);
              setOpen(false);
            }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-slate-700 transition-colors hover:bg-blue-50 hover:text-blue-700" data-id="element-68" data-mp="2e1kof0vo0woz">
                    <CheckIcon className="h-3.5 w-3.5 shrink-0 text-transparent" data-id="element-69" data-mp="1v40c0ta37ql9" />
                    <span className="truncate" data-id="element-70" data-mp="201i9nninmjdy">Move to {t.title}</span>
                  </button>
                </li>)}
              {targets.length === 0 && <li className="px-3 py-3 text-center text-[11px] text-slate-400" data-id="element-71" data-mp="3bi9ooq4yvqw9">
                  No other sections available.
                </li>}
            </ul>
          </div>
        </>}
    </>;
}